<script>
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import BaseCheckbox from '@/components/_base-checkbox.vue'
import HoverClickPopup from '@/components/booking/bookingList/hover-click-popup.vue'
import BookingFeedbackModal from '@/components/booking/feedback/booking-feedback-modal.vue'
import CancelBookingDialog from '@components/booking/cancel-booking-dialog.vue'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import BookingCard from '@/shared/constants/booking/BookingCard'
import Interaction from '@/models/general/interaction'
import InteractionType from '@/shared/constants/core/InteractionType'
import EmitEvent from '@/models/general/emitEvent'
import MenuItem from '@/models/general/menuItem'
import BaseDropdownMenu from '@/components/_base-dropdown-menu.vue'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingStatusLabel from '@components/booking/booking-status-label.vue'
import dayjs from '@/services/date'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import DateColumn from '@/components/booking/bookingList/booking-row-column/date-column.vue'
import EducatorColumn from '@/components/booking/bookingList/booking-row-column/educator-column.vue'
import BookingOptionsSheet from '@/components/booking/bookingList/booking-row-column/booking-options-sheet.vue'
import CustomTooltip from '@components/booking/bookingList/custom-tooltip.vue'
import BookingType from '@/shared/constants/booking/BookingType'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'

export default {
  name: 'BookingRow',
  components: {
    CustomTooltip,
    BaseCheckbox,
    HoverClickPopup,
    BookingFeedbackModal,
    CancelBookingDialog,
    BaseDropdownMenu,
    BookingStatusLabel,
    DateColumn,
    EducatorColumn,
    BookingOptionsSheet,
  },
  mixins: [baseMixin],
  props: {
    booking: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    showNotes: { type: Boolean, default: false },
    activeTab: { type: [String, Number], default: '' },
    currentTabName: { type: String, default: '' },
  },
  data: () => {
    return {
      isBookingOptions: false,
      feedbackDialog: false,
      quickFeedbackSuccess: false,
      bookingToCancel: null,
      showCancelBookingDialog: false,
      BookingStatusLabelType,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      isSubmittingQuickFeedback: 'bookings/isSubmittingQuickFeedback',
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingCreatePermission:
        'auth/hasLocationBookingCreatePermission',
    }),
    isMobile() {
      return this.isMobileViewPort
    },
    isDayByDay() {
      return !!this.activeTab
    },
    blockRange() {
      // if day-by-day, no need for block range
      if (this.isDayByDay) return null

      // if no blockDetails, fallback to booking start/end
      if (
        !Array.isArray(this.booking.blockDetails) ||
        !this.booking.blockDetails.length
      ) {
        if (this.booking.startDateTimeLocal && this.booking.endDateTimeLocal) {
          return {
            minStart: new Date(this.booking.startDateTimeLocal),
            maxEnd: new Date(this.booking.endDateTimeLocal),
          }
        }
        return null
      }

      // collect blockDetails normally
      const starts = this.booking.blockDetails
        .map((b) => new Date(b.startDateTimeLocal))
        .filter((d) => !isNaN(d))
      const ends = this.booking.blockDetails
        .map((b) => new Date(b.endDateTimeLocal))
        .filter((d) => !isNaN(d))
      if (!starts.length || !ends.length) return null

      const minStart = new Date(Math.min(...starts))
      const maxEnd = new Date(Math.max(...ends))
      return { minStart, maxEnd }
    },
    bookingTime() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(DateFormatToken.HHmm) +
        ' - ' +
        dayjs(this.booking.endDateTimeLocal).format(DateFormatToken.HHmm)
      )
    },
    blockTimes() {
      // if day-by-day, no need for block range
      if (this.isDayByDay) return null

      // fallback if no blockDetails → use booking start/end
      if (
        !Array.isArray(this.booking.blockDetails) ||
        !this.booking.blockDetails.length
      ) {
        if (this.booking.startDateTimeLocal && this.booking.endDateTimeLocal) {
          const fmt = (d) => dayjs(d).format(DateFormatToken.HHmm)

          return {
            vary: false,
            start: fmt(this.booking.startDateTimeLocal),
            end: fmt(this.booking.endDateTimeLocal),
          }
        }
        return null
      }

      // handle blockDetails normally
      const fmt = (d) => dayjs(d).format(DateFormatToken.HHmm)

      const starts = this.booking.blockDetails.map((b) =>
        fmt(b.startDateTimeLocal)
      )
      const ends = this.booking.blockDetails.map((b) => fmt(b.endDateTimeLocal))

      const allStartSame = starts.every((t) => t === starts[0])
      const allEndSame = ends.every((t) => t === ends[0])

      if (allStartSame && allEndSame) {
        return { vary: false, start: starts[0], end: ends[0] }
      }

      const pairs = [
        ...new Set(
          this.booking.blockDetails.map(
            (b) => `${fmt(b.startDateTimeLocal)} - ${fmt(b.endDateTimeLocal)}`
          )
        ),
      ]

      return { vary: true, variations: pairs.join(', ') }
    },
    bookingId() {
      return this.booking?.id
    },
    feedback() {
      return this.booking?.feedback
    },
    feedbackProvided() {
      return this.feedback.provided || this.quickFeedbackSuccess
    },
    isUnfilled() {
      return this.booking.bookingStatus === BookingCard.labelStatus.InProgress
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    buttonConfig() {
      const {
        warning200,
        blueBgHover,
        warning,
        blue,
        grey500,
      } = VuetifyColorClass
      return {
        hoverColor: this.isUnfilled ? warning200 : blueBgHover,
        iconColor: grey500,
        iconHoverColor: this.isUnfilled ? warning : blue,
      }
    },
    isPastBooking() {
      if (this.blockRange) {
        return new Date(this.blockRange.maxEnd) < new Date()
      }
      return new Date(this.booking.endDateTimeLocal) < new Date()
    },
    isUpcomingBooking() {
      return (
        !this.isPastBooking &&
        this.booking.bookingStatus === BookingStatusLabelType.Open
      )
    },
    dropdownItems() {
      if (this.currentTabName === 'upcoming') {
        const options = this.isUpcomingBooking ? this.cardOptions : []
        return [
          ...this.editBookingOptions,
          ...this.duplicateBookingOptions,
          ...options,
        ]
      } else if (this.currentTabName === 'past') {
        const past = this.feedbackCardOptions
        return [...this.duplicateBookingOptions, ...past]
      } else if (this.currentTabName === 'all') {
        const upcoming = this.isUpcomingBooking ? this.cardOptions : []
        const past = this.isPastBooking ? this.feedbackCardOptions : []
        return [
          ...this.editBookingOptions,
          ...this.duplicateBookingOptions,
          ...upcoming,
          ...past,
        ]
      }
      return []
    },
    cardOptions() {
      const options = [
        {
          icon: 'mdi-close-circle-outline',
          type: 'action',
          text: this.$t('booking.cancelBookingBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'cancelBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    feedbackCardOptions() {
      const options = [
        {
          icon: 'mdi-message-alert-outline',
          type: 'action',
          text: this.$t('feedback.provideFeedbackBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'showFeedback',
              value: this.booking.id,
            }),
          }),
        },
      ]

      return options.map((option) => new MenuItem(option))
    },
    editBookingOptions() {
      const isToday =
        new Date(this.booking.startDateTimeLocal).toDateString() ===
        new Date().toDateString()

      const allBlocksOpenAndFuture =
        this.booking.blockDetails &&
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.every(
          (block) =>
            block.bookingStatus === BookingStatusLabelType.Open &&
            new Date(block.startDateTimeLocal) > new Date()
        )

      const bookingCount = this.booking.blockDetails
        ? this.booking.blockDetails.length
        : 0

      const options = [
        {
          id: isToday
            ? 'edit-today-booking-menu-item'
            : 'edit-day-booking-menu-item',
          icon: isToday ? 'mdi-pencil-outline' : 'mdi-calendar-edit-outline',
          type: 'action',
          text: isToday
            ? this.$t('booking.edit')
            : this.$t('booking.editBookingDay'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingDay',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.canCreateBookings,
            this.isDayByDay,
            this.booking.bookingStatus === BookingStatusLabelType.Open,
            new Date(this.booking.startDateTimeLocal) >=
              new Date(new Date().setHours(0, 0, 0, 0)),
          ],
          data: {
            disabled: isToday,
          },
        },
        {
          icon: 'mdi-pencil-outline',
          type: 'action',
          text: this.$t('booking.editBookingBlock'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingBlock',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.canCreateBookings,
            this.booking.bookingStatus === BookingStatusLabelType.Open,
            allBlocksOpenAndFuture,
            bookingCount === this.booking.openCount,
          ],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    isValidDuplicateStatus() {
      const validStatuses = [
        BookingStatusLabelType.Open,
        BookingStatusLabelType.Pending,
        BookingStatusLabelType.Confirmed,
      ]

      let isValidStatus = validStatuses.includes(this.booking.bookingStatus)

      if (!this.isDayByDay) {
        const blockDetails = this.booking.blockDetails
        const hasBlocks = Array.isArray(blockDetails) && blockDetails.length > 0
        if (hasBlocks) {
          isValidStatus = this.booking.blockDetails.every((block) =>
            validStatuses.includes(block.bookingStatus)
          )
        }
      }

      return isValidStatus
    },
    duplicateBookingOptions() {
      const options = [
        {
          id: 'duplicate-booking-menu-item',
          icon: 'mdi-content-copy',
          type: 'action',
          text: this.$t('booking.duplicate'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'duplicateBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
          data: {
            disabled: !this.isValidDuplicateStatus,
          },
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
  },
  methods: {
    ...mapActions({
      requestFeedbackFormUrl: 'bookings/requestFeedbackFormUrl',
      submitQuickFeedback: 'bookings/submitQuickFeedback',
    }),
    viewCandidate(candidate) {
      if (candidate && candidate.id != null) {
        this.$emit('viewCandidate', candidate.id)
      }
    },
    updateShowNotes(val) {
      this.$emit('update-show-notes', val)
    },
    viewSidepanel(bookingDetails) {
      this.$emit('sidePanel', bookingDetails)
    },
    openBookingOptions() {
      if (
        this.booking.bookingStatus === BookingStatusLabelType.Open &&
        (this.currentTabName === 'upcoming' || this.currentTabName === 'all')
      ) {
        this.isBookingOptions = true
      } else if (
        this.currentTabName === 'past' ||
        (this.currentTabName === 'all' && this.isPastBooking)
      ) {
        this.isBookingOptions = true
      } else {
        this.isBookingOptions = false
      }
    },
    openFeedback() {
      this.feedbackDialog = true
    },
    handleFeedbackProvided() {
      this.quickFeedbackSuccess = true
    },
    openCancelBookingDialog() {
      this.bookingToCancel = this.booking
      this.showCancelBookingDialog = true
    },
    closeCancelBookingDialog() {
      this.bookingToCancel = null
      this.showCancelBookingDialog = false
    },
    async closeCancelBookingDialogAndRefresh() {
      this.closeCancelBookingDialog()
      await this.refreshDashboardBookingData()
    },
    async handleApproveRejectBooking() {
      this.$emit('activeBookingId', this.booking.id)
    },
    disableMenuItem(menuItem) {
      if (!menuItem) return false

      switch (menuItem.id) {
        case 'edit-today-booking-menu-item':
        case 'duplicate-booking-menu-item':
          return menuItem.data.disabled
        default:
          return false
      }
    },
    editBookingDay() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        query: {
          mode: BookingType.SingleDay,
          bookingId: this.booking.id,
          date: dayjs(this.booking.startDateTimeLocal).format(
            DateFormatToken.YYYYMMDD
          ),
        },
      })
    },
    editBookingBlock() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        query: {
          mode: BookingType.Block,
          bookingId: this.booking.id,
        },
      })
    },
    duplicateBooking() {
      const bookingMode = !this.isDayByDay ? BookingType.Block : BookingType.SingleDay

      const query = {
        mode: bookingMode,
        bookingId: this.booking.id,
      }

      if (this.isDayByDay) {
        query.date = dayjs(this.booking.startDateTimeLocal).format(DateFormatToken.YYYYMMDD)
      }

      this.$router.push({
        name: routeDefinitions.bookingsCreate.name,
        query
      })
    },
  },
}
</script>

<template>
  <tr class="booking-row" :class="{ 'selected-row': selected }">
    <!-- Checkbox (mobile) -->
    <td class="checkbox-cell">
      <CustomTooltip
        position="right"
        :text="
          selected
            ? $t('customeToolTip.unselectBooking')
            : $t('customeToolTip.selectBooking')
        "
      >
        <BaseCheckbox
          md
          class="custom-checkbox"
          :model-value="selected"
          @update:modelValue="$emit('toggle-select')"
        />
      </CustomTooltip>
    </td>

    <DateColumn
      ref="dateColumn"
      :booking="booking"
      :is-mobile="isMobile"
      :current-tab-name="currentTabName"
      :active-tab="activeTab"
      :booking-time="bookingTime"
      :is-day-by-day="isDayByDay"
      :block-range="blockRange"
      :block-times="blockTimes"
      :view-sidepanel="viewSidepanel"
    />

    <!-- DAYS column (only when not day-by-day OR approval) -->
    <td
      v-if="!isMobile && (!activeTab || currentTabName === 'ApprovalRequired')"
      @click.stop="viewSidepanel(booking)"
    >
      <span class="days-count">{{ booking.days }}</span>
      <span v-if="booking.openCount && booking.openCount > 0" class="open-days">
        {{ booking.openCount }} {{ $t('general.open') }}
      </span>
    </td>

    <EducatorColumn
      ref="educatorColumn"
      :booking="booking"
      :is-mobile="isMobile"
      :current-tab-name="currentTabName"
      :is-day-by-day="isDayByDay"
      :booking-status-label-type="BookingStatusLabelType"
      :view-sidepanel="viewSidepanel"
      @viewCandidate="viewCandidate"
    />

    <!-- TIME (desktop) -->
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      <template v-if="isDayByDay">
        {{ bookingTime }}
      </template>
      <template v-else-if="blockRange">
        <template v-if="booking.timeVaries">
          <div class="times-vary">
            <CustomTooltip :text="blockTimes.variations">{{
              $t('bookingList.timesVary')
            }}</CustomTooltip>
          </div>
        </template>
        <template v-else
          >{{ blockTimes.start }} - {{ blockTimes.end }}</template
        >
      </template>
    </td>

    <!-- STATUS (mobile) -->
    <td
      v-if="
        (isMobile || (isDayByDay && !isMobile)) &&
          currentTabName !== 'ApprovalRequired' &&
          currentTabName !== 'cancelled'
      "
      @click.stop="viewSidepanel(booking)"
    >
      <BookingStatusLabel
        :class="[
          'status-cell status-label',
          booking.bookingStatus.toLowerCase(),
        ]"
        :status="booking.bookingStatus"
      />
    </td>

    <!-- CLASSIFICATION / ROOM / REPLACING / GRADE / CERTS (desktop) -->
    <td
      v-if="currentTabName === 'ApprovalRequired'"
      @click.stop="viewSidepanel(booking)"
    >
      {{ booking.classification.name }}
    </td>
    <td v-else-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{ booking.classification.name }}
    </td>
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{ booking.roomNumber ? booking.roomNumber : '—' }}
    </td>
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{
        booking.replacing && booking.replacing.firstName
          ? booking.replacing.firstName
          : ''
      }}
      {{
        booking.replacing && booking.replacing.lastName
          ? booking.replacing.lastName
          : ''
      }}
    </td>
    <td
      v-if="!isMobile"
      class="text-no-wrap"
      @click.stop="viewSidepanel(booking)"
    >
      {{
        booking.grades && booking.grades.length
          ? booking.grades.map((g) => g.title).join(', ')
          : '—'
      }}
    </td>
    <td
      v-if="!isMobile"
      class="small-cell d-flex fill-height"
      @click.stop="viewSidepanel(booking)"
    >
      <span
        v-for="(cert, i) in booking.certificationTypes"
        :key="`certificate-${i}`"
        :class="i % 2 === 0 ? 'certification' : 'notes'"
        >{{ cert }}</span
      >
    </td>

    <!-- CLIENT column when approval/cancelled -->
    <td
      v-if="
        !isMobile &&
          (currentTabName === 'ApprovalRequired' ||
            currentTabName === 'cancelled')
      "
      class="text-no-wrap"
      @click.stop="viewSidepanel(booking)"
    >
      {{ booking.client ? booking.client.name : '—' }}
    </td>

    <!-- Actions / notes (desktop) -->
    <td
      v-if="!isMobile && currentTabName !== 'ApprovalRequired'"
      style="text-align: end"
      class="notes-col"
      :class="[{ 'show-notes': showNotes }, { 'selected-row': selected }]"
    >
      <div
        class="d-flex notes-cell"
        :class="{ 'no-dropdown': dropdownItems.length === 0 }"
      >
        <HoverClickPopup
          :text="booking.clientNotes"
          @popup-toggle="updateShowNotes"
        />
        <BaseDropdownMenu
          v-if="dropdownItems.length > 0"
          :data-button="`booking-options-${booking.id}`"
          v-bind="buttonConfig"
          :menu-items="dropdownItems"
          tertiary
          :disable-list-item="disableMenuItem"
          @duplicateBooking="duplicateBooking"
          @editBookingDay="editBookingDay"
          @editBookingBlock="editBookingBlock"
          @cancelBooking="openCancelBookingDialog"
          @showFeedback="openFeedback"
          @menu-state-changed="updateShowNotes"
        />
      </div>
    </td>

    <!-- Approve/Decline (desktop) -->
    <td
      v-else-if="!isMobile"
      style="text-align: end"
      class="ApprovalRequired-col"
      :class="[{ 'show-notes': showNotes }, { 'selected-row': selected }]"
    >
      <div class="d-flex approved-cell">
        <BaseButton
          text
          outlined
          light
          width="81"
          height="40"
          :border-radius="'8px'"
          class="decline-button"
          @click="handleApproveRejectBooking"
        >
          {{ $t('customeToolTip.decline') }}
        </BaseButton>
        <BaseButton
          width="89"
          height="40"
          :border-radius="'8px'"
          color="primary"
          @click="handleApproveRejectBooking"
        >
          {{ $t('customeToolTip.approve') }}
        </BaseButton>
      </div>
    </td>

    <!-- Right dots (mobile) -->
    <td
      v-if="isMobile && currentTabName !== 'ApprovalRequired'"
      class="right-dots"
    >
      <v-icon small @click="openBookingOptions">mdi-dots-vertical</v-icon>
    </td>
    <td v-else-if="isMobile" class="right-cols">
      <CustomTooltip
        :text="$t('customeToolTip.decline')"
        style="margin-right: 10px"
      >
        <div class="decline-button" @click="handleApproveRejectBooking">
          <v-icon size="20">mdi-close</v-icon>
        </div>
      </CustomTooltip>
      <CustomTooltip :text="$t('customeToolTip.approve')">
        <div class="approve-button" @click="handleApproveRejectBooking">
          <v-icon size="20">mdi-check</v-icon>
        </div>
      </CustomTooltip>
    </td>
    <!-- Bottom sheet Feedback & Cancel in Mobile view -->
    <BookingOptionsSheet
      v-model="isBookingOptions"
      :current-tab-name="currentTabName"
      :is-past-booking="isPastBooking"
      :is-upcoming-booking="isUpcomingBooking"
      :is-valid-duplicate-status="isValidDuplicateStatus"
      :edit-booking-options="editBookingOptions"
      @editBookingDay="editBookingDay"
      @editBookingBlock="editBookingBlock"
      @duplicateBooking="duplicateBooking"
      @feedback="openFeedback"
      @cancelBooking="openCancelBookingDialog"
    />
    <BookingFeedbackModal
      v-model="feedbackDialog"
      :booking="booking"
      @feedbackProvided="handleFeedbackProvided"
    />
    <CancelBookingDialog
      v-if="showCancelBookingDialog"
      :show="showCancelBookingDialog"
      :booking="bookingToCancel"
      @closeModal="closeCancelBookingDialog"
      @closeModalAndRefresh="closeCancelBookingDialogAndRefresh"
    />
  </tr>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-row.scss';
</style>
